import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../CDN';
import { ContactUsMode } from '../components/forms/ContactForm';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import ParallaxLayoutList, { ParallaxLayoutTextListCard } from '../components/layout/lists/ParallaxLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiScrollingLogo } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import ScrollingLogosWidget from '../widgets/animated/ScrollingLogosWidget';
import BenefitsCards, { BenefitsCardsPageSectionWrapper } from '../widgets/cards/BenefitsCards';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import { FeatureShowcase } from '../widgets/showcases/FeatureShowcase/FeatureShowcase';

type BenefitsPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<BenefitsPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const props: BenefitsPageStaticProps = { scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const BenefitsPage: NextPageWithLayout<BenefitsPageStaticProps> = (props) => {
  const { scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Benefits);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar AI Benefits"
        title="Save money, boost productivity"
        description="Reap the benefits of Generative AI in the workplace. Offer magical employee assistance and boost efficiency across the enterprise."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <BenefitsCardsPageSectionWrapper>
        <BenefitsCards
          records={[
            {
              imageUrl: CDNAssets.getUrl('benefit-reduce-manual-work'),
              title: 'Reduce manual work by 80%',
              description: 'Our no-code automation enables you to minimize repetitive work.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-lower-cost'),
              title: 'Lower helpdesk ticket cost to $1',
              description: 'By automating tasks and better managing knowledge, you save time and money.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-fast-resolution'),
              title: 'Resolution in 10 seconds',
              description: 'Employees get instant solutions and helpdesk teams have fewer tickets to handle.',
            },
          ]}
        />
      </BenefitsCardsPageSectionWrapper>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle mode="parallax" text="Benefits of Gaspar AI in the workplace" />
        <ParallaxLayoutList
          renderItem={(item) => <ParallaxLayoutTextListCard {...item} />}
          items={[
            {
              title: 'IT department',
              data: {
                description: 'Boost productivity and trust in the employee helpdesk',
                entries: [
                  'Reduce helpdesk ticket cost to only $1: With Gaspar AI you decrease resolution time and the cost of inefficient time, thus lowering related costs. Consequently, your company saves money and increases profitability.',
                  'Streamline operations: Empower your workforce to seamlessly find instant solutions and answers, automate tasks and manage knowledge across all your business systems effortlessly.',
                  "Improve company safety and security: Oftentimes employees hesitate to contact your helpdesk team to avoid wasting time. They might resort to risky shortcuts, like using personal accounts or unsafe sources to resolve issues quickly, putting corporate safety at risk. Gaspar AI's swift and dependable employee issue resolution encourages them to seek help from IT instead of resorting to unsafe fixes. Plus, our automated offboarding streamlines the process, reducing the time it takes to de-provision employees and safeguarding company data.",
                ],
              },
            },
            {
              title: 'Across company',
              data: {
                description: 'Lower costs and enhance company protection',
                entries: [
                  'Auto-resolve 40% of tickets: Gaspar, our AI Virtual Agent, resolves employee issues directly on Slack, MS Teams and JSM support portal, and automates tasks without human intervention. Your busy teams no longer answer the same questions over and over and have fewer tickets to deal with.',
                  'Resolve issues in seconds, not hours: Gaspar solves employee requests instantly and accurately, in natural language, deflecting a high number of tickets. Fewer tickets to be handled means faster resolution time even for requests that need to be addressed by human agents.',
                  'Improve the perception of IT company-wide: 1/5 of employees claim tech issues and resolution times are the biggest challenges. With Gaspar AI, you can offer fast and accurate solutions to their most frequent problems, so that IT becomes their best partner.',
                ],
              },
            },
            {
              title: 'Employees',
              data: {
                description: 'Increase employee happiness by 85%',
                entries: [
                  'By adopting Gaspar AI, employees get immediate answers from our AI Virtual Agent directly on Slack, MS Teams or Jira SM. This streamlined communication leads to perfect employee experiences: it frees their time and makes them more productive, leading to higher satisfaction, retention, and a great workplace culture.',
                  "Provide fast and 100% secure onboarding: Employee onboarding is very time-consuming and can be frustrating. With Gaspar AI's workflows and HR helpdesk features it can be fully automated, accelerating service delivery while ensuring compliance with the company's policies.",
                ],
              },
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Why Gaspar AI" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-1'),
              orientation: 'left',
              title: 'Automate countless tasks from day one',
              description:
                'No coding, no training – Gaspar AI works autonomously to address all employee support issues across IT, HR, and Sales instantly. Deployment takes 30 minutes to 1 hour, with no training or manual scripting needed.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-2'),
              orientation: 'right',
              title: 'Boost usage by engaging employees anywhere',
              description:
                'The only platform with an AI chatbot offering in-app instant support on 3 applications - Slack, MS Teams and JSM support portal. With continuously evolving integrations, Gaspar AI provides a sophisticated chat experience, featuring in-bot form completion when in need to raise a ticket, and precise solutions.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-3'),
              orientation: 'left',
              title: 'Automatically make company knowledge available',
              description:
                "Employees can access hard-to-find resources seamlessly on their preferred support channel. Gaspar AI integrates with your knowledge base and internal repositories to automatically index and ingest information, to match employees' requests with concise solutions. Business Insights highlight resource effectiveness, helping teams fill in knowledge gaps.",
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-4'),
              orientation: 'right',
              title: 'Understand employees no matter how they speak',
              description:
                'Gaspar AI has been built on advanced LLMs, and trained on quality data sets specific to the employee support sector, ensuring instant understanding of issues and human-like, accurate answers. Our Natural Language Processing (NLP) and Natural Language Understanding (NLU) technology outperforms competition and can be trained within weeks on a new language. Thanks to our dialog management capabilities, our Virtual Agent can ask follow-up questions and clarifications until the employee’s issue is resolved.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-5'),
              orientation: 'left',
              title: 'Automate processes with no-code workflows',
              description:
                "With Gaspar AI's workflows you can easily build and automate complex processes, and auto-fulfill repetitive tasks such as employee onboarding. No coding skills needed! Leveraging our easy-to-use workflow templates, you can create customized flows for business processes and watch as Gaspar AI implements them automatically.",
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-6'),
              orientation: 'right',
              title: 'Automate approvals and ticket routing',
              description:
                'With Gaspar AI you can expedite support for approvals and complex issues. Approvals are directly handled in a few minutes’ time on Slack, MS Teams or JSM, with status updates and notifications. For issues that require an expert, tickets are easily created by filling in a form on Slack or MS Teams, and then automatically routed to the right agent to be handled promptly.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefits-page-why-gaspar-7'),
              orientation: 'left',
              title: 'Unprecedented service management',
              description:
                'Thanks to Gaspar AI’s Business Insights and Performance Metrics you get actionable recommendations for your support team and business operations. You can easily review your platform’s performance, get guidance about how to further optimize processes, fill in knowledge gaps and prioritize automation opportunities, greatly impacting your bottom line.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.BlueElectric}>
        <FeatureShowcase
          title="Drive your workforce forward"
          description="Elevate your IT team's potential, improve employee experience, transform HR operations, utilize your company's knowledge efficiently and boost productivity with Gaspar AI's helpdesk."
          panels={[{ imageUrl: CDNAssets.getUrl('benefits-page-showcase-demo') }]}
        />
      </PageSection>
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
    </>
  );
};
BenefitsPage.displayName = 'BenefitsPage';
export default BenefitsPage;
